import React, { useEffect, useState } from 'react';
import PlusIcon from '@heroicons/react/outline/PlusIcon.js';
import { v4 as uuidv4 } from 'uuid';
import { PostTypeContext } from '@tribeplatform/gql-client/types';
import { PickerFormControlSpace } from '@tribeplatform/react-components/EntityPicker';
import { T, useI18n } from '@tribeplatform/react-components/i18n';
import { ImagePickerBlockField } from '@tribeplatform/react-components/PickerField';
import { useSpace, useSpacePostTypes } from '@tribeplatform/react-sdk/hooks';
import { simplifyPaginatedResult } from '@tribeplatform/react-sdk/utils';
import { Button } from '@tribeplatform/react-ui-kit/Button';
import { FormControl } from '@tribeplatform/react-ui-kit/FormControl';
import { useBlock, useSlate } from '../../hooks/index.js';
export const PostComposerBlockSettings = () => {
    var _a, _b, _c;
    const { $t } = useI18n();
    const { block: { props: { buttons }, }, upsertProp, } = useBlock();
    const [activeIndex, setActiveIndex] = useState(0);
    const { context: { spaceId }, } = useSlate();
    const [activeItemSpaceId, setActiveItemSpaceId] = useState(((_a = buttons === null || buttons === void 0 ? void 0 : buttons[0]) === null || _a === void 0 ? void 0 : _a.spaceId) || spaceId);
    const { data: activeItemSpace } = useSpace({
        variables: {
            id: activeItemSpaceId,
        },
        fields: {
            image: 'basic',
        },
        useQueryOptions: {
            enabled: !!activeItemSpaceId,
        },
    });
    const { data, isFetched: isSpacePostTypesFetched } = useSpacePostTypes({
        variables: {
            spaceId: activeItemSpaceId,
            limit: 30,
            context: PostTypeContext.post,
        },
        useInfiniteQueryOptions: {
            enabled: !!activeItemSpaceId,
        },
    });
    const { nodes: spacePostTypes } = simplifyPaginatedResult(data);
    const postTypeId = (_b = buttons === null || buttons === void 0 ? void 0 : buttons[activeIndex]) === null || _b === void 0 ? void 0 : _b.postTypeId;
    const [activeItemPostTypeId, setActiveItemPostTypeId] = useState(postTypeId);
    useEffect(() => {
        var _a, _b;
        if (!(buttons === null || buttons === void 0 ? void 0 : buttons.length) && isSpacePostTypesFetched) {
            const postTypeId = ((_a = spacePostTypes === null || spacePostTypes === void 0 ? void 0 : spacePostTypes.find(t => t.postType.slug === 'tribe-discussion')) === null || _a === void 0 ? void 0 : _a.postTypeId) || ((_b = spacePostTypes === null || spacePostTypes === void 0 ? void 0 : spacePostTypes[0]) === null || _b === void 0 ? void 0 : _b.postTypeId);
            const newButton = {
                id: uuidv4(),
                text: $t({
                    id: 'Blocks.PostComposer.DefaultButtonText',
                    defaultMessage: 'What’s on your mind?',
                }),
                spaceId,
                postTypeId,
                primary: true,
            };
            if (postTypeId) {
                setActiveItemPostTypeId(postTypeId);
                setActiveItemSpaceId(spaceId);
            }
            else {
                delete newButton.spaceId;
                setActiveItemSpaceId(null);
            }
            buttons.push(newButton);
        }
    }, [spacePostTypes, isSpacePostTypesFetched, buttons, spaceId, $t]);
    const addItem = () => {
        const newItems = [
            ...buttons,
            {
                text: $t({
                    id: 'Blocks.PostComposer.NewPostItemText',
                    defaultMessage: 'New post',
                }),
                primary: false,
                id: uuidv4(),
                spaceId,
            },
        ];
        const index = newItems.length - 1;
        upsertProp('buttons', newItems);
        setActiveItemSpaceId(spaceId);
        setActiveItemPostTypeId(null);
        setActiveIndex(index);
    };
    const deleteItem = (buttons, key, itemIndex) => {
        upsertProp(key, [
            ...buttons.slice(0, itemIndex),
            ...buttons.slice(itemIndex + 1),
        ]);
    };
    const editItem = (index, key, value) => {
        const newButtons = [...buttons];
        newButtons[index][key] = value;
        upsertProp(`buttons`, newButtons);
        setActiveIndex(index);
    };
    const makePrimary = index => {
        const newButtons = [...buttons];
        for (let i = 0; i < (buttons === null || buttons === void 0 ? void 0 : buttons.length); i++) {
            newButtons[i].primary = index === i;
        }
        upsertProp(`buttons`, newButtons);
    };
    return (React.createElement("div", { className: "space-y-5" },
        buttons.map((item, index) => (React.createElement("div", { key: item === null || item === void 0 ? void 0 : item.id }, index === activeIndex ? (React.createElement("div", { className: "space-y-3 bg-surface-subdued p-3 rounded-md" },
            React.createElement(FormControl, null,
                React.createElement(FormControl.Label, null,
                    React.createElement(T, { id: "Generics.Image", defaultMessage: "Image" })),
                React.createElement(ImagePickerBlockField, { image: item.icon, removable: true, onChange: icon => {
                        editItem(index, 'icon', icon);
                    } })),
            React.createElement(FormControl.Input, { name: "text", label: $t({ id: 'Generics.Text', defaultMessage: 'Text' }), value: item.text, onChange: e => editItem(index, 'text', e.target.value) }),
            React.createElement(PickerFormControlSpace, { name: "space", placeholder: $t({
                    id: 'Blocks.PostComposer.SelectSpacePlaceholder',
                    defaultMessage: 'Select a {SPACE}...',
                }), label: $t({
                    id: 'Generics.Space',
                    defaultMessage: '{SPACE_CC}',
                }), value: activeItemSpace, onChange: space => {
                    setActiveItemSpaceId(space === null || space === void 0 ? void 0 : space.id);
                    editItem(index, 'spaceId', space === null || space === void 0 ? void 0 : space.id);
                } }),
            React.createElement(FormControl.Select, { name: "defaultPostTypeId", value: activeItemPostTypeId, onChange: (value) => {
                    setActiveItemPostTypeId(value);
                    editItem(index, 'postTypeId', value);
                    return value;
                }, label: $t({
                    id: 'Blocks.PostComposer.SelectPostTypeLabel',
                    defaultMessage: 'Primary post type',
                }), placeholder: $t({
                    id: 'Blocks.PostComposer.SelectPostTypePlaceholder',
                    defaultMessage: 'Pick a post type...',
                }), items: spacePostTypes === null || spacePostTypes === void 0 ? void 0 : spacePostTypes.filter(it => it.postType.context === PostTypeContext.post).map(spacePostType => {
                    var _a;
                    return ({
                        text: (_a = spacePostType === null || spacePostType === void 0 ? void 0 : spacePostType.postType) === null || _a === void 0 ? void 0 : _a.name,
                        value: spacePostType === null || spacePostType === void 0 ? void 0 : spacePostType.postTypeId,
                    });
                }) }),
            React.createElement(FormControl.Toggle, { name: "primary", checked: item.primary, label: $t({
                    id: 'Blocks.PostComposer.PrimaryButtonLabel',
                    defaultMessage: 'Primary button',
                }), onChange: value => {
                    if (value) {
                        makePrimary(index);
                    }
                    else {
                        editItem(index, 'primary', false);
                    }
                } }),
            (buttons === null || buttons === void 0 ? void 0 : buttons.length) > 1 && (React.createElement(Button, { variant: "outline", onClick: () => deleteItem(buttons, 'buttons', index) },
                React.createElement(T, { id: "Generics.Remove", defaultMessage: "Remove" }))))) : (React.createElement("div", { className: "space-y-3 bg-surface-subdued p-3 rounded-md cursor-pointer", onClick: () => {
                setActiveIndex(index);
                setActiveItemSpaceId(buttons[index].spaceId);
                setActiveItemPostTypeId(buttons[index].postTypeId);
            } }, item.text || (React.createElement(T, { id: "Blocks.PostComposer.DefaultButtonText", defaultMessage: "What\u2019s on your mind?" }))))))),
        ((_c = buttons.filter(button => !(button === null || button === void 0 ? void 0 : button.primary))) === null || _c === void 0 ? void 0 : _c.length) < 3 && (React.createElement(Button, { variant: "outline", onClick: () => addItem(), fullWidth: true, leadingIcon: React.createElement(PlusIcon, null) },
            React.createElement(T, { id: "Blocks.PostComposer.AddButtonButtonText", defaultMessage: "Add button" })))));
};
//# sourceMappingURL=Settings.js.map